import { trapFocus, removeTrapFocus } from '@theme/focus';

class MobileNavDrawer extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.panels = this.querySelector('[data-panels]');
    this.panelMain = this.querySelector('[data-panel-main]');
    this.panelSub = this.querySelector('[data-panel-sub]');
    this.subTitle = this.querySelector('[data-sub-title]');
    this.subUrl = this.querySelector('[data-sub-url]');
    this.subContent = this.querySelector('[data-sub-content]');
    this.drawer = this.querySelector('.mobile-nav__drawer');

    this.summary.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.details.open) {
        this.close();
      } else {
        this.open();
      }
    });

    this.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-submenu-trigger]');
      if (trigger) {
        e.preventDefault();
        this.openSubmenu(trigger);
        return;
      }

      const back = e.target.closest('[data-back]');
      if (back) {
        this.closeSubmenu();
        return;
      }

      /* Backdrop and close buttons share [data-close] */
      const close = e.target.closest('[data-close]');
      if (close) {
        this.close();
        return;
      }
    });

    this._onKeydown = (e) => {
      if (e.key === 'Escape') this.close();
    };
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKeydown);
    removeTrapFocus();
  }

  open() {
    /* Offset drawer to cover full viewport (contain: layout on ancestor breaks fixed) */
    const containingBlock = this.closest('header-component') || this.parentElement;
    const offset = containingBlock.getBoundingClientRect().top;
    this.drawer.style.top = `${-offset}px`;

    this.details.open = true;
    this.summary.setAttribute('aria-expanded', 'true');
    /* Allow the DOM to paint the off-screen state before sliding in */
    requestAnimationFrame(() => {
      this.panels.dataset.drawerOpen = '';
    });
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this._onKeydown);
    trapFocus(this.panels);
  }

  close() {
    delete this.panels.dataset.drawerOpen;
    this.summary.setAttribute('aria-expanded', 'false');

    let settled = false;
    const onEnd = () => {
      if (settled) return;
      settled = true;
      this.details.open = false;
      this.resetSubmenu();
    };

    this.panels.addEventListener('transitionend', onEnd, { once: true });
    /* Fallback if transition doesn't fire (e.g. prefers-reduced-motion) */
    setTimeout(onEnd, 250);

    document.body.style.overflow = '';
    document.removeEventListener('keydown', this._onKeydown);
    removeTrapFocus();
    this.summary.focus();
  }

  openSubmenu(trigger) {
    const title = trigger.dataset.submenuTitle || '';
    const url = trigger.dataset.submenuUrl || '#';
    const template = trigger.nextElementSibling;

    if (!template || !template.matches('[data-submenu-content]')) return;

    this._lastSubmenuTrigger = trigger;
    trigger.setAttribute('aria-expanded', 'true');

    this.subTitle.textContent = title;
    this.subUrl.href = url;
    this.subUrl.setAttribute('aria-label', `${this.subUrl.textContent.trim()} ${title}`);
    this.subUrl.hidden = url === '#' || url === '';

    const content = template.content.cloneNode(true);
    this.subContent.replaceChildren(content);

    this.panels.dataset.panelActive = 'sub';

    const backBtn = this.panelSub.querySelector('[data-back]');
    if (backBtn) {
      let settled = false;
      const onEnd = () => {
        if (settled) return;
        settled = true;
        backBtn.focus({ preventScroll: true });
      };
      this.panelSub.addEventListener('transitionend', onEnd, { once: true });
      setTimeout(onEnd, 250);
    }
  }

  closeSubmenu() {
    this.panels.dataset.panelActive = 'main';

    const trigger = this._lastSubmenuTrigger;
    this._lastSubmenuTrigger = null;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');

    let settled = false;
    const cleanup = () => {
      if (settled) return;
      settled = true;
      if (this.panels.dataset.panelActive !== 'sub') {
        this.subContent.replaceChildren();
      }
    };

    this.panelSub.addEventListener('transitionend', cleanup, { once: true });

    /* Fallback if transition doesn't fire (e.g. prefers-reduced-motion) */
    setTimeout(cleanup, 250);

    if (trigger) trigger.focus();
  }

  resetSubmenu() {
    this.panels.dataset.panelActive = 'main';
    this.subContent.replaceChildren();
    this.subUrl.hidden = true;
  }
}

if (!customElements.get('mobile-nav-drawer')) {
  customElements.define('mobile-nav-drawer', MobileNavDrawer);
}
